import { TypedDocumentNode, gql } from '@apollo/client';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import styled, { CSSProperties, keyframes } from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';

import { SeasonalityLabel } from 'components/lineup/SeasonalityLabel';
import { LineupRankingDumb } from 'layouts/LineupLayout/LineupRanking/Dumb';

import { LeaderboardDivisionChangeRecap_so5Ranking } from './__generated__/index.graphql';

export const DELAY_BEFORE_ANIMATION_MS = 3500;

const cardEnter = keyframes`
  from {
    opacity: 0.2;
    transform: translateX(-10%) translateY(20%) rotateY(-70deg);
  }
  to {
    opacity: 1;
    transform: translateX(0%) translateY(0%) rotateY(0deg);
  }
`;
const infoEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(30%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;
const divisionEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(2.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;
const iconUpEnter = keyframes`
  0% {
    opacity: 0;
    transform: translateY(50%);
  }
  5% {
    opacity: 1;
    transform: translateY(0%);
  }
  65% {
    opacity: 1;
    transform: translateY(0%);
  }
  70% {
    opacity: 0;
    transform: translateY(-50%);
  }
`;
const iconDownEnter = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-50%);
  }
  5% {
    opacity: 1;
    transform: translateY(0%);
  }
  65% {
    opacity: 1;
    transform: translateY(0%);
  }
  70% {
    opacity: 0;
    transform: translateY(50%);
  }
`;
const InnerWrapper = styled(Vertical).attrs({ gap: 3 })`
  --border-color: var(--c-score-high);
  --bg-color-1: 34, 252, 95;

  position: relative;
  aspect-ratio: 210 / 340;
  border: 4px solid var(--border-color);
  border-radius: var(--double-and-a-half-unit);
  padding-top: var(--double-unit);
  opacity: 0;
  overflow: hidden;

  animation: ${cardEnter} 0.5s ease-in-out var(--delay) forwards;

  background:
    radial-gradient(
      60% 27% at 50% 106.3%,
      rgba(var(--bg-color-1), 0.5) 0%,
      rgba(var(--bg-color-1), 0) 100%
    ),
    linear-gradient(180deg, transparent 20%, var(--c-black) 70%),
    var(--picture-url) no-repeat 0 30px / cover,
    linear-gradient(
      180deg,
      rgba(var(--bg-color-1), 0.2) 0%,
      rgba(var(--bg-color-1), 0) 49.95%
    ),
    var(--c-black);

  &.isRelegation {
    --border-color: var(--c-static-red-600);
    --bg-color-1: 242, 59, 39;
  }

  & > *:first-child {
    flex: 1;
  }
`;
const FullHeightVertical = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
  flex: 1;
  justify-content: space-between;
`;
const UpDownIcon = styled(Vertical).attrs({ gap: 0 })`
  padding: 0 var(--double-unit);
  align-self: flex-start;

  & > *:first-child {
    opacity: 0;

    animation: ${iconUpEnter} 4s ease-out calc(var(--delay) + 1200ms) infinite;
    .isRelegation & {
      animation: ${iconDownEnter} 4s ease-out calc(var(--delay) + 1400ms)
        infinite;
    }
  }
  & > *:nth-child(2) {
    margin-top: calc(-1 * var(--double-unit));
    opacity: 0;

    animation: ${iconUpEnter} 4s ease-out calc(var(--delay) + 1400ms) infinite;
    .isRelegation & {
      animation: ${iconDownEnter} 4s ease-out calc(var(--delay) + 1200ms)
        infinite;
    }
  }
`;
const DivisionIconImgWrapper = styled(Horizontal).attrs({ center: true })`
  flex: 1;
`;
const DivisionIconImg = styled.img`
  opacity: 0;
  animation: ${divisionEnter} 0.4s ease-out calc(var(--delay) + 700ms) forwards;
`;
const SeasonalityLabelWrapper = styled.div`
  opacity: 0;
  animation: ${infoEnter} 0.4s ease-out calc(var(--delay) + 550ms) forwards;
`;
const ManagerTeamName = styled(BodyM)`
  text-align: center;
  opacity: 0;
  animation: ${infoEnter} 0.4s ease-out calc(var(--delay) + 500ms) forwards;
`;
const LineupRanking = styled(LineupRankingDumb)`
  width: 100%;
`;
const LeagueHeader = styled.img`
  width: 100%;
  max-height: calc(5 * var(--unit));
`;

const findAppearanceWithBestScore = (
  appearances: LeaderboardDivisionChangeRecap_so5Ranking['so5Lineup']['so5Appearances']
):
  | LeaderboardDivisionChangeRecap_so5Ranking['so5Lineup']['so5Appearances'][number]
  | undefined => {
  return appearances.reduce(
    (acc, cur) =>
      (cur.so5Score?.score || 0) > (acc.so5Score?.score || 0) ? cur : acc,
    appearances[0]
  );
};

type Props = {
  so5Ranking: LeaderboardDivisionChangeRecap_so5Ranking;
  delay: number;
};
export const LeaderboardDivisionChangeRecap = ({
  so5Ranking,
  delay,
}: Props) => {
  const { so5Lineup, so5Leaderboard, eligibleDivisionChange, score } =
    so5Ranking;
  const isRelegation = eligibleDivisionChange === DivisionChange.RELEGATED;

  const { so5League } = so5Leaderboard;
  const { managerTeam } = so5Lineup;
  const bestAppearance = findAppearanceWithBestScore(so5Lineup.so5Appearances);

  const chevronIcon = isRelegation ? (
    <FontAwesomeIcon
      icon={faChevronDown}
      color="var(--c-static-red-600)"
      size="xl"
    />
  ) : (
    <FontAwesomeIcon icon={faChevronUp} color="var(--c-score-high)" size="xl" />
  );
  return (
    <InnerWrapper
      className={classNames({ isRelegation })}
      style={
        {
          '--picture-url': `url(${bestAppearance?.player.pictureUrl})`,
          '--delay': `${delay}ms`,
        } as CSSProperties
      }
    >
      <Vertical gap={2} center>
        <UpDownIcon>
          {chevronIcon}
          {chevronIcon}
        </UpDownIcon>
        {managerTeam?.activeDivisionIconUrl && (
          <DivisionIconImgWrapper>
            <DivisionIconImg
              // this works since we only display LastFixtureStory for latest gameweek
              // (no other promotion/relegation in between)
              src={managerTeam.activeDivisionIconUrl}
              alt=""
              width="70px"
              height="70px"
            />
          </DivisionIconImgWrapper>
        )}
        <FullHeightVertical>
          <Vertical gap={0} center>
            <SeasonalityLabelWrapper>
              <BodyS as="p">
                <SeasonalityLabel
                  seasonality={so5Leaderboard.seasonality}
                  label={so5Leaderboard.seasonalityName}
                />
              </BodyS>
            </SeasonalityLabelWrapper>
            <ManagerTeamName bold>{managerTeam?.name}</ManagerTeamName>
          </Vertical>
          <LineupRanking
            ranking={so5Ranking}
            score={score}
            showRankingAndScore
          />
          <LeagueHeader src={so5League?.headerUrl} alt="" />
        </FullHeightVertical>
      </Vertical>
    </InnerWrapper>
  );
};

LeaderboardDivisionChangeRecap.fragments = {
  so5Ranking: gql`
    fragment LeaderboardDivisionChangeRecap_so5Ranking on So5Ranking {
      id
      ranking
      rankingRatio
      eligibleDivisionChange
      score
      so5Leaderboard {
        slug
        seasonality
        seasonalityName
        so5League {
          slug
          headerUrl
        }
      }
      so5Lineup {
        id
        managerTeam {
          id
          name
          activeDivisionIconUrl
        }
        so5Appearances {
          id
          player {
            slug
            pictureUrl(derivative: "squared")
          }
          so5Score {
            id
            score
          }
        }
      }
    }
  ` as TypedDocumentNode<LeaderboardDivisionChangeRecap_so5Ranking>,
};
